import { useState, useRef } from 'react';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import useAuth from '../../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';


export default function StepAdd({ isNavOpen, customRoles }) {
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();
  const userId = auth?.userId;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const maxFileSize =
    Number(process.env.REACT_APP_MAX_FILE_SIZE) || 300;

  console.log('maxFileSize', maxFileSize)

  // Step data
  const [stepName, setStepName] = useState('');
  const [stepDescription, setStepDescription] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [stepNameError, setStepNameError] = useState('');


  // Upload data
  const [videoFile, setVideoFile] = useState(null);
  const [audioFile, setAudioFile] = useState(null);


  // 🎙 Audio recording
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudioURL, setRecordedAudioURL] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // UI
  const [isUploading, setIsUploading] = useState(false);
  const [messages, setMessages] = useState([]);
  const currentVideoMsgIdRef = useRef(null);
  const videoSuccessShownRef = useRef(false);


  // Upload UX
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [videoProcessing, setVideoProcessing] = useState(false);

  const [navigateToStep, setNavigateToStep] = useState(false); // ✅ Default: stay
  const [stayHere, setStayHere] = useState(true);

  const videoInputRef = useRef(null);
  const audioInputRef = useRef(null);

  const hasBlockingError = messages.some(
    m => m.key === 'video-too-large' || m.key === 'audio-too-large'
  );

  // 🧱 Message helpers
  const addMessage = (text, { temporary = false } = {}) => {
    const id = Date.now() + Math.random();
    setMessages((prev) => [...prev, { id, text, temporary }]);
    return id; // return ID if we want to remove later
  };

  const removeMessage = (id) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== id));
  };

  const setUniqueMessage = (key, text) => {
    setMessages((prev) => [
      ...prev.filter((m) => m.key !== key),
      { id: Date.now(), key, text }
    ]);
  };


  // Audio file selection
  const handleAudioChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('audio/')) {
      addMessage(t('addStep.pleaseUploadValid'));
      e.target.value = null;
      setAudioFile(null);
      return;
    }

    setAudioFile(file);
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxBytes = maxFileSize * 1024 * 1024;

    if (file.size > maxBytes) {
      setUniqueMessage(
        'video-too-large',
        t('addStep.fileTooLarge', { maxSize: maxFileSize })
      );
      e.target.value = null;
      setVideoFile(null);
      return;
    }



    setMessages((prev) => prev.filter((m) => m.key !== 'video-too-large'));
    setVideoFile(file);
  };


  // 🧠 Upload helper functions
  const uploadVideo = async (stepId, stepSlug) => {
    if (!videoFile) return null;

    const formData = new FormData();
    formData.append('video', videoFile);
    formData.append('userId', userId);


    setVideoUploadProgress(0);
    setVideoProcessing(false);


    try {
      const res = await axiosPrivate.post(
        `/videos/upload/${stepId}/${stepSlug}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 0,
          onUploadProgress: (e) => {
            const percent = Math.round((e.loaded * 100) / e.total);
            setVideoUploadProgress(percent);

            if (currentVideoMsgIdRef.current) {
              removeMessage(currentVideoMsgIdRef.current);
            }

            currentVideoMsgIdRef.current = addMessage(
              t('addStep.uploadingVideoProgress', { percent }),
              { temporary: true }
            );


            if (percent === 100) {
              setVideoProcessing(true);
            }
          },

        }
      );

      setVideoProcessing(false);

      setTimeout(() => {
        setVideoUploadProgress(0);
      }, 2000);

      if (currentVideoMsgIdRef.current) {
        removeMessage(currentVideoMsgIdRef.current);
        currentVideoMsgIdRef.current = null;
      }

      setVideoUploadProgress(0);
      setVideoProcessing(false);



      if (!videoSuccessShownRef.current) {
        addMessage(t('addStep.videoSuccessDelayed'));
        videoSuccessShownRef.current = true;
      }


      return res.data?.videoPath || true;

    } catch (err) {
      setVideoProcessing(false);

      if (currentVideoMsgIdRef.current) {
        removeMessage(currentVideoMsgIdRef.current);
        currentVideoMsgIdRef.current = null;
      }


      // ⏱️ give backend time to finish writing
      await new Promise(r => setTimeout(r, 1000));

      try {
        const checkRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/media/videos/${stepId}/${stepSlug}.mp4?cacheBust=${Date.now()}`,
          {
            headers: { Authorization: `Bearer ${auth.accessToken}` },
          }
        );

        if (checkRes.ok) {
          if (!videoSuccessShownRef.current) {
            addMessage(t('addStep.videoSuccessDelayed'));
            videoSuccessShownRef.current = true;
          }

          return true;
        }
      } catch (_) { }

      const status = err.response?.status;
      const errorText =
        err.response?.data?.error ||
        err.response?.data?.message ||
        '';

      if (
        status === 413 ||
        errorText.includes('File is too large') ||
        errorText.includes('LIMIT_FILE_SIZE')
      ) {
        setUniqueMessage(
          'video-too-large',
          t('addStep.fileTooLarge', { maxSize: maxFileSize })
        );
        return null;
      }

      if (status === 403 && errorText === 'STORAGE_LIMIT_REACHED') {
        addMessage(t('stepPage.storageLimitReached'));
        return null;
      }

      addMessage(t('addStep.errorUploadingVideo'));
      return null;

    }
  };



  const uploadAudio = async (stepId, stepSlug) => {
    if (!audioFile) return null;

    const formData = new FormData();
    formData.append('audio', audioFile);
    formData.append('userId', userId);



    try {
      const res = await axiosPrivate.post(
        `/audios/upload/${stepId}/${stepSlug}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      addMessage(t('addStep.audioSuccess'));
      return res.data.audioPath;
    } catch (err) {

      const serverMessage = err.response?.data?.error || '';
      if (err.response?.status === 413 || serverMessage.includes('File is too large')) {
        setUniqueMessage(
          'audio-too-large',
          t('addStep.fileTooLarge', { maxSize: maxFileSize })
        );
      } else {
        addMessage(`${t('addStep.errorUploadingAudio')} ${err.response?.data?.message || err.message}`);
      }
      return null;
    }
  };

  const startAudioRecording = async () => {

    if (isUploading) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, {
          type: mediaRecorder.mimeType || 'audio/webm',
        });

        if (blob.size < 1000) {
          addMessage(t('addStep.emptyRecording'));
          setIsRecording(false);
          return;
        }

        const fileExtension = blob.type.includes('mp4') ? 'mp4' : 'webm';

        const file = new File(
          [blob],
          `recording_${Date.now()}.${fileExtension}`,
          { type: blob.type }
        );

        setMessages(prev => prev.filter(m => m.key !== 'audio-too-large'));


        // ✅ StepAdd uses setAudioFile
        setAudioFile(file);
        setRecordedAudioURL(URL.createObjectURL(blob));

        // ✅ Release mic (Safari-safe)
        mediaRecorder.stream
          ?.getTracks()
          .forEach(track => track.stop());
      };



      mediaRecorder.start(1000); // ⏱️ force dataavailable every 1s (Safari fix)
      setIsRecording(true);

      // ⏱️ AUTO-STOP after 30 seconds
      setTimeout(() => {
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.requestData(); // ✅ Safari flush
          mediaRecorder.stop();
          setIsRecording(false);
        }

      }, 30_000); // 30 seconds

    } catch (err) {
      console.error('Microphone access denied:', err);
      addMessage(t('addStep.microphonePermissionDenied'));
    }
  };

  const stopAudioRecording = () => {
    if (isUploading) return;

    const recorder = mediaRecorderRef.current;
    if (!recorder) return;

    // 🧠 Safari needs this to flush audio
    if (recorder.state === 'recording') {
      recorder.requestData(); // ✅ CRITICAL for iOS
      recorder.stop();
    }

    setIsRecording(false);
  };


  const clearRecordedAudio = () => {
    setAudioFile(null);
    setRecordedAudioURL(null);
  };


  // 🧩 Step creation
  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsUploading(true);
    videoSuccessShownRef.current = false;
    setMessages(prev => prev.filter(m => !m.temporary));
    try {
      // 1️⃣ Create the step
      const createRes = await axiosPrivate.post('/steps/create', {
        step_name: stepName,
        step_description: stepDescription,
        created_by: userId,
        role_ids: selectedRoleIds,
      });

      const { step_id, step_slug } = createRes.data;
      if (!videoFile) {
        addMessage(`${t('addStep.stepCreated')} ${stepName}`);
      }

      // 2️⃣ Upload media
      if (videoFile) {
        const videoResult = await uploadVideo(step_id, step_slug);
        if (!videoResult) {
          setIsUploading(false);
          return; // ⛔ STOP HERE
        }
      }

      if (audioFile) {
        const audioResult = await uploadAudio(step_id, step_slug);
        if (!audioResult) {
          setIsUploading(false);
          return;
        }
      }

      // 3️⃣ Reset form
      setStepName('');
      setStepDescription('');
      setSelectedRoleIds([]);
      setVideoFile(null);
      setAudioFile(null);
      if (videoInputRef.current) videoInputRef.current.value = '';
      if (audioInputRef.current) audioInputRef.current.value = '';

      // 4️⃣ Navigate or stay
      if (navigateToStep) {
        navigate(`/roles/${customRoles[0].role_slug}/steps/${step_id}`);
      }

    } catch (err) {
      const serverMessage = err.response?.data?.message || '';

      // ✅ Duplicate step name → localized error
      if (
        err.response?.status === 409 ||
        serverMessage.toLowerCase().includes('exists')
      ) {
        setStepNameError(t('stepPage.stepExistsError'));
        return;
      }

      // Fallback generic error
      addMessage(`❌ ${t('addStep.errorCreatingStep')}`);
    } finally {
      setIsUploading(false);
    }

  };

  // 🧱 Render
  return (
    <div className={`${isNavOpen ? 'body-squeezed' : 'body'}`}>
      <div className='tx1'>
        <h2>{t('addStep.createNewStep')}</h2>

        <form onSubmit={handleSubmit} style={{ maxWidth: '500px' }}>
          <fieldset disabled={isUploading} style={{ border: 'none', padding: 0 }}>
            {/* Step name */}
            <label>
              {t('addStep.stepName')}
              <input
                type="text"
                value={stepName}
                onChange={(e) => {
                  setStepName(e.target.value);
                  setStepNameError('');
                  setMessages(prev => prev.filter(m => !m.temporary));
                }}
                pattern="^[A-Za-zÀ-ÖØ-öø-ÿ0-9._\- ]+$"
                title={t('addStep.stepNameInputTitle')}
                required
                style={{ width: '100%', marginBottom: '1rem' }}
              />
            </label>

            {stepNameError && (
              <div style={{ color: 'red', fontSize: '0.9rem', marginBottom: '1rem' }}>
                {stepNameError}
              </div>
            )}

            {/* Description */}
            <label>
              {t('addStep.stepDescription')}
              <textarea
                value={stepDescription}
                onChange={(e) => setStepDescription(e.target.value)}
                rows="4"
                style={{ width: '100%', marginBottom: '1rem' }}
              />
            </label>

            {/* Roles */}
            <label>{t('addStep.stepRoles')}</label>
            <div style={{ marginBottom: '1rem' }}>
              {customRoles.map((role) => (
                <label key={role.role_id} style={{ display: 'block' }}>
                  <input
                    type="checkbox"
                    value={role.role_id}
                    checked={selectedRoleIds.includes(role.role_id)}
                    onChange={(e) => {
                      const id = parseInt(e.target.value);
                      if (e.target.checked) {
                        setSelectedRoleIds([...selectedRoleIds, id]);
                      } else {
                        setSelectedRoleIds(selectedRoleIds.filter((r) => r !== id));
                      }
                    }}
                  />
                  {role.role_name}
                </label>
              ))}
            </div>

            {/* Uploads */}
            <div style={{ marginBottom: '1rem' }}>
              <label>
                {t('addStep.uploadVideo')}
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleVideoChange}
                />
                {videoFile && (
                  <button
                    type="button"
                    onClick={() => {
                      setVideoFile(null);
                      if (videoInputRef.current) videoInputRef.current.value = '';
                    }}
                    style={{
                      marginLeft: '0.5rem',
                      cursor: 'pointer',
                      border: 'none',
                      background: 'transparent',
                      color: 'red',
                      fontWeight: 'bold',
                    }}
                  >
                    ×
                  </button>

                )}
              </label>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label>{t('addStep.uploadAudio')}</label>

              {/* 📁 Upload audio */}
              <input
                ref={audioInputRef}
                type="file"
                accept="audio/*"
                onChange={handleAudioChange}
                disabled={isRecording}
              />

              {/* 🎙 Recording controls */}
              <div style={{ marginTop: '0.5rem' }}>
                {!isRecording ? (
                  <button
                    type="button"
                    className="button-white"
                    onClick={startAudioRecording}
                    disabled={isUploading}
                  >
                    🎙 {t('addStep.recordAudio')}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="button-white"
                    onClick={stopAudioRecording}
                  >
                    ⏹ {t('addStep.stopRecording')}
                  </button>
                )}
              </div>

              {/* ▶️ Preview */}
              {recordedAudioURL && (
                <div style={{ marginTop: '0.5rem' }}>
                  <audio controls src={recordedAudioURL} />
                </div>
              )}

              {/* ❌ Clear */}
              {audioFile && (
                <button
                  type="button"
                  onClick={clearRecordedAudio}
                  style={{
                    marginLeft: '0.5rem',
                    cursor: 'pointer',
                    border: 'none',
                    background: 'transparent',
                    color: 'red',
                    fontWeight: 'bold',
                  }}
                >
                  ×
                </button>
              )}
            </div>


            {/* ✅ Navigation checkboxes */}
            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label>
                <input
                  type="checkbox"
                  checked={navigateToStep}
                  onChange={() => {
                    setNavigateToStep(true);
                    setStayHere(false);
                  }}
                />
                {t('addStep.navigateToStep')}
              </label>

              <label>
                <input
                  type="checkbox"
                  checked={stayHere}
                  onChange={() => {
                    setStayHere(true);
                    setNavigateToStep(false);
                  }}
                />
                {t('addStep.stayHere')}
              </label>
            </div>

            {videoFile && videoUploadProgress > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <progress value={videoUploadProgress} max="100" style={{ width: '100%' }} />
                <small>
                  {videoUploadProgress < 100
                    ? t('addStep.uploadingVideoProgress', { percent: videoUploadProgress })
                    : t('addStep.videoProcessingNotice')}
                </small>
              </div>
            )}

          </fieldset>

          {/* Create Step */}
          <button
            type="submit"
            className="button-white"
            disabled={
              stepName.trim() === '' ||
              selectedRoleIds.length === 0 ||
              isUploading ||
              hasBlockingError
            }
          >
            {isUploading ? t('addStep.processing') : t('addStep.createStep')}
          </button>
        </form>

        {/* Messages */}
        {/* {messages.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            {messages.map((msg) => (
              <p key={msg.id}>{msg.text}</p>
            ))}
          </div>
        )} */}

        {messages.some(m => !m.temporary) && (
          <div style={{ marginTop: '1rem', color: 'red' }}>
            {messages.filter(m => !m.temporary).map(msg => (
              <p key={msg.id}>{msg.text}</p>
            ))}
          </div>
        )}


      </div>
    </div>
  );
}
